import React, { useState } from 'react';
import PropTypes from 'prop-types';

export const SlidingPanelsContext = React.createContext();

/**
 * Utility component to organize SlidingPanels.
 *
 * SlidingPanel must be an immediate child.
 *
 * @example
 *    <SlidingPanelGroup>
 *      <SlidingPanel />
 *      <SlidingPanel />
 *      <SlidingPanel />
 *    </SlidingPanelGroup>
 */
const SlidingPanelsProvider = ({
  children,
  initialActivePanels,
  customRoot,
  isOpen,
  onEveryClose
}) => {
  const [activePanels, setActivePanels] = useState(initialActivePanels);

  /**
   * Remove a SlidingPanel from the list of the active (open) SlidingPanels
   *
   * @param {string} panelId "PanelId" Prop of the SlidingPanel
   * @returns void
   */
  const removePanel = (panelId) => {
    setActivePanels(
      activePanels.filter((activePanelId) => activePanelId !== panelId)
    );
  };

  /**
   * Add a SlidingPanel to the list of active (open) SlidingPanels
   *
   * @param {string} panelId "PanelId" Prop of the SlidingPanel
   * @returns void
   */
  const addPanel = (panelId) => {
    /**
     * TO DO:
     *
     * Create logic to add z-index for each active panel,
     * so the last activated panel will be on top.
     *
     * This would be helpful if there were PanelItem links
     * that linked to other SlidingPanels with a lesser
     * z-index.
     *
     * i.e. PanelItem links linking to
     * other "parent" SlidingPanels
     *
     * This is not a feature on the legacy site,
     * so preventing over engineering at the moment.
     */
    setActivePanels([...activePanels, panelId]);
  };

  /**
   * Remove all SlidingPanels from list of active
   * (open) SlidingPanels
   *
   * @returns void
   */
  const closeAll = () => {
    setActivePanels([]);
  };

  return (
    <SlidingPanelsContext.Provider
      value={{ activePanels, closeAll, addPanel, removePanel }}
    >
      {React.Children.map(children, (child, i) => {
        /**
         * Do nothing if child is not a SlidingPanel.
         *
         * `child.type.name` could be checked, but that would not work
         * if the component is composed into a higher order component.
         * `props.panelId` is required on SlidingPanel.
         */
        if (!child.props.panelId) {
          return child;
        }

        const {
          props: { onClose, panelId }
        } = child;

        return React.cloneElement(child, {
          /**
           * This SlidingPanel should display if:
           * SlidingPanels.context is open
           * AND if either of the following is true:
           *  - assume first SlidingPanel is root, which should always display
           *  - AND display panels that are active
           */
          isOpen: isOpen && (activePanels.includes(panelId) || i === 0),
          isRoot: customRoot === child.props.panelId || i === 0,
          onClose: (e) => {
            /**
             * It  might be necessary to attach a unique `onClose` function to a `SlidingPanel`
             * along with the `onEveryClose` function, which is called on all`SlidingPanel` components:
             *
             * @example
             *
             *  <SlidingPanelsGroup onEveryClose={handleEveryClose} />
             *    <SlidingPanel panelId="Activity" onClose={uniqueHandleJustForActivity} />
             *    <SlidingPanel panelId="Colors"  />
             *    <SlidingPanel panelId="Sock Height" />
             *   </SlidingPanelsGroup>
             *
             * When "Activity" is closed, `handleEveryClose` *and* `uniqueHandleJustForActivity` are called.
             */

            if (onClose) {
              onClose(e);
            }

            if (onEveryClose) {
              onEveryClose(e);
            }
          }
        });
      })}
    </SlidingPanelsContext.Provider>
  );
};

SlidingPanelsProvider.propTypes = {
  /**
   * React child elements
   */
  children: PropTypes.node,
  /**
   * Panels that should start in the active state on mount
   */
  initialActivePanels: PropTypes.arrayOf(PropTypes.string),
  /**
   * Specifies the ID of a custom root panel, if it isn't the first panel
   */
  customRoot: PropTypes.string,
  /**
   * Whether or not the group of
   * SlidingPanels is open
   */
  isOpen: PropTypes.bool,
  /**
   * Handler to apply for each close icon
   * in each SlidingPanel.
   *
   * @params {SyntheticBaseEvent} e React synthetic event
   */
  onEveryClose: PropTypes.func
};

SlidingPanelsProvider.defaultProps = {
  children: null,
  initialActivePanels: [],
  customRoot: null,
  isOpen: false,
  onEveryClose: null
};

export default SlidingPanelsProvider;
